/**
 * Tag Editor Selector Service
 * Manages tag selector modal and tag insertion flow
 */

import { Injectable } from '@angular/core'
import { TranslocoService } from '@jsverse/transloco'
import { EditorInteractionService } from './editor-interaction.service'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { TagEditorTreeNode } from '../tag-editor-tree-node'
import { COMPONENT_STRINGS, TAG_EDITOR_STRINGS } from '../tag-editor.constants'

/**
 * Callbacks for selector operations
 */
export interface SelectorCallbacks {
  readonly onOpen: () => void
  readonly onClose: () => void
  readonly onNodeSelect: (node: TagEditorTreeNode, selectedText: string) => void
  readonly onSelectionValidate: (selectedText: string) => boolean
  readonly onMarkForCheck: () => void
}

@Injectable({ providedIn: 'root' })
export class TagEditorSelectorService {
  constructor(
    private readonly editorInteractionService: EditorInteractionService,
    private readonly stateService: TagEditorStateService,
    private readonly debugLoggingService: DebugLoggingService,
    private readonly translocoService: TranslocoService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  /**
   * Opens tag selector modal
   */
  public openSelector(_editor: HTMLElement, callbacks: SelectorCallbacks): void {
    this.debugLog(' openTagSelector called')

    const currentSelection: Selection | null = window.getSelection()
    this.debugLog(' Current selection before save:', {
      hasSelection: !!currentSelection,
      rangeCount: currentSelection?.rangeCount || 0,
      selectedText: currentSelection?.toString() || TAG_EDITOR_STRINGS.EMPTY_STRING,
      isCollapsed: currentSelection?.isCollapsed,
    })

    const savedRange: Range | null = this.editorInteractionService.saveSelection()
    this.stateService.setSavedRange(savedRange)
    this.stateService.setTagSelectorOpen(true)
    this.debugLog(' Saved selection:', savedRange)

    callbacks.onOpen()
    this.debugLog(' Tag selector modal opened')
  }

  /**
   * Closes tag selector modal
   */
  public closeSelector(callbacks: SelectorCallbacks): void {
    callbacks.onClose()
    this.stateService.setSavedRange(null)
    this.stateService.setTagSelectorOpen(false)
  }

  /**
   * Handles node selection from tree
   */
  public handleNodeSelect(node: TagEditorTreeNode, editor: HTMLElement, callbacks: SelectorCallbacks): void {
    this.debugLog(' Tree node selected:', node)

    if (!node || !node.data) {
      this.debugLog(' Invalid node or node data, returning.')
      return
    }

    const savedRange: Range | null = this.stateService.getSavedRange()
    if (savedRange) {
      this.editorInteractionService.restoreSelection(savedRange)
      this.debugLog(' Restored saved selection.')
    } else {
      this.debugLog(' No saved selection found.')
    }

    const selectedText: string = this.getSelectedText(editor)
    if (!selectedText) {
      const alertMessage: string = this.translocoService.translate('TAG_EDITOR.NO_TEXT_SELECTED') as string
      alert(alertMessage)
      this.closeSelector(callbacks)
      this.debugLog(' No text selected, closing tag selector.')
      return
    }
    this.debugLog(' Selected text:', selectedText)

    if (!callbacks.onSelectionValidate(selectedText)) {
      return
    }

    callbacks.onNodeSelect(node, selectedText)
    this.debugLog(' Inserted tag, now closing selector.')

    // Close selector UI, but keep selector flag set until insertion completes
    callbacks.onClose()
    this.stateService.setSavedRange(null)
    // Note: isTagSelectorOpen flag is cleared by insertion service after insertion completes
  }

  /**
   * Validates that selection exists
   */
  public validateSelection(editor: HTMLElement): boolean {
    const selectedText: string = this.getSelectedText(editor)
    return !!(selectedText && selectedText.trim().length > 0)
  }

  /**
   * Gets selected text from editor
   */
  public getSelectedText(_editor: HTMLElement): string {
    return this.editorInteractionService.getSelectedText()
  }
}
